import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../api/client";
import type { DayPattern, DemandAnalysis, ScheduleImportRow } from "../../api/types";
import type { GeneratorDraft } from "../demand/DemandCard";
import { type Dir, type PlanParams, fmtHHMM, fmtRiders, stationLabel } from "../demand/demandMath";
import DayReplay from "./DayReplay";
import Scorecard, { type Run } from "./Scorecard";
import TimeDistanceChart from "./TimeDistanceChart";
import WaitingTable from "./WaitingTable";
import { stripSummary } from "./scorecardMath.ts";
import { buildInput, simulate } from "./simulate.ts";
import "./simulation.css";

/** A timetable the page holds, ready to be run. */
export interface TimetableSource {
  label: string;
  rows: ScheduleImportRow[];
}

interface Props {
  routeId: string;
  dayPattern: DayPattern;
  /** Look-back in days: the same period the demand card averages over. */
  days: number;
  plan: PlanParams;
  onPlan: (p: PlanParams) => void;
  /** The timetable running today for this route and day pattern, when there is one. */
  active: TimetableSource | null;
  /** What the generator currently holds; null for a viewer without a generator. */
  draft: GeneratorDraft | null;
  /** The draft's simulated figures, for the generator's own summary line. */
  onDraftSummary?: (summary: string | null) => void;
}

type View = "active" | "draft" | "both";

const FACTORS = [0.8, 0.9, 1, 1.1, 1.2, 1.5];

const WINDOWS: Array<{ label: string; range: [number, number] }> = [
  { label: "Morning", range: [300, 600] },
  { label: "Midday", range: [600, 900] },
  { label: "Evening", range: [900, 1200] },
  { label: "Late", range: [1200, 1440] },
];

/**
 * Runs the timetable against the measured riders, minute by minute, and shows
 * what riders would get: how full each trip is where, who is left standing at
 * a stop and for how long, and what it costs in buses. The active timetable
 * and the generator's draft can be run side by side, so an edit to the bands
 * can be tested before it is activated.
 */
export default function SimulationCard({ routeId, dayPattern, days, plan, onPlan, active, draft, onDraftSummary }: Props) {
  const [demand, setDemand] = useState<DemandAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [factor, setFactor] = useState(1);
  const [patience, setPatience] = useState(30);
  const [view, setView] = useState<View>("both");
  const [win, setWin] = useState<[number, number] | null>(null);
  const [clock, setClock] = useState<number | null>(null);
  const [seek, setSeek] = useState<{ min: number; seq: number } | null>(null);

  // Riders at the time they reach the stop, in the finest slices the mirror
  // keeps: a queue is built from arrivals, not from trip departures.
  const requestSeq = useRef(0);
  useEffect(() => {
    if (!routeId) { setDemand(null); return; }
    const seq = ++requestSeq.current;
    setLoading(true);
    api.getDemand({ route_id: routeId, day_pattern: dayPattern, days, bucket: 15, time_basis: "boarding" })
      .then((res) => {
        if (seq !== requestSeq.current) return;
        setDemand(res);
        setError(null);
      })
      .catch((err) => {
        if (seq !== requestSeq.current) return;
        setDemand(null);
        setError(err instanceof Error ? err.message : "Could not load ridership");
      })
      .finally(() => { if (seq === requestSeq.current) setLoading(false); });
  }, [routeId, dayPattern, days]);

  const hasDemand = !!demand && demand.period.days_included > 0 && demand.buckets.length > 0;
  const haveActive = !!active && active.rows.length > 0;
  const haveDraft = !!draft?.rows && draft.rows.length > 0;

  const params = useMemo(
    () => ({ busCapacity: plan.busCapacity, demandFactor: factor, maxWaitMin: patience }),
    [plan.busCapacity, factor, patience]
  );

  const activeRun = useMemo<Run | null>(
    () => (hasDemand && demand && active && haveActive ? { label: "Active timetable", result: simulate(buildInput(demand, active.rows, params)) } : null),
    [hasDemand, demand, active, haveActive, params]
  );
  const draftRun = useMemo<Run | null>(
    () => (hasDemand && demand && draft?.rows && haveDraft ? { label: "Draft", result: simulate(buildInput(demand, draft.rows, params)) } : null),
    [hasDemand, demand, draft?.rows, haveDraft, params]
  );

  useEffect(() => {
    onDraftSummary?.(draftRun ? stripSummary(draftRun.result.totals, activeRun?.result.totals ?? null) : null);
  }, [draftRun, activeRun, onDraftSummary]);

  // What is shown: the choice when both exist, otherwise whatever there is.
  const shown: View | null =
    activeRun && draftRun ? view
    : activeRun ? "active"
    : draftRun ? "draft"
    : null;
  const runs = useMemo<Run[]>(() => {
    if (shown === "both") return [activeRun!, draftRun!];
    if (shown === "active") return [activeRun!];
    if (shown === "draft") return [draftRun!];
    return [];
  }, [shown, activeRun, draftRun]);

  const dirNames = useMemo<Record<Dir, string>>(() => {
    const stops = demand?.stops ?? [];
    if (stops.length < 2) return { upward: "upward", downward: "downward" };
    return { upward: `to ${stationLabel(stops[stops.length - 1].name)}`, downward: `to ${stationLabel(stops[0].name)}` };
  }, [demand]);

  const onTimeChange = useCallback((min: number) => setClock(min), []);
  const onPickTime = useCallback((min: number) => setSeek((s) => ({ min, seq: (s?.seq ?? 0) + 1 })), []);

  const ridersPerDay = demand ? demand.totals.journeys * factor : 0;

  return (
    <section className="card dm-card sim-card" id="schedule-simulation" aria-busy={loading}>
      <div className="dm-head">
        <div>
          <h3>Simulate the day</h3>
          <p className="secondary-text">
            The timetable run against the measured riders, minute by minute: how full each trip gets, who is left at
            the stop and for how long, and what it costs in buses. Test a change here before activating it.
          </p>
        </div>
      </div>

      <div className="dm-filters">
        {activeRun && draftRun && (
          <div className="sched-field">
            <span>Timetable</span>
            <div className="sched-segmented">
              <button type="button" className={view === "active" ? "active" : ""} onClick={() => setView("active")}>Active</button>
              <button type="button" className={view === "draft" ? "active" : ""} onClick={() => setView("draft")}>Draft</button>
              <button type="button" className={view === "both" ? "active" : ""} onClick={() => setView("both")}>Compare both</button>
            </div>
          </div>
        )}
        <label className="sched-field sim-field-capacity">
          <span>Bus capacity</span>
          <div className="sched-suffix-field">
            <input type="number" min={10} max={300} value={plan.busCapacity}
              onChange={(e) => {
                const v = Math.round(Number(e.target.value));
                if (Number.isFinite(v)) onPlan({ ...plan, busCapacity: Math.min(300, Math.max(10, v)) });
              }} />
            <span className="sched-suffix">riders</span>
          </div>
        </label>
        <label className="sched-field">
          <span>Ridership</span>
          <select value={factor} onChange={(e) => setFactor(Number(e.target.value))}>
            {FACTORS.map((f) => (
              <option key={f} value={f}>{f === 1 ? "As measured" : `${f > 1 ? "+" : "−"}${Math.round(Math.abs(f - 1) * 100)}%`}</option>
            ))}
          </select>
        </label>
        <label className="sched-field sim-field-patience" title="A rider who has waited this long without getting on a bus leaves, and is counted as having had no bus.">
          <span>Riders give up after</span>
          <div className="sched-suffix-field">
            <input type="number" min={5} max={180} step={5} value={patience}
              onChange={(e) => {
                const v = Math.round(Number(e.target.value));
                if (Number.isFinite(v)) setPatience(Math.min(180, Math.max(5, v)));
              }} />
            <span className="sched-suffix">min</span>
          </div>
        </label>
        {hasDemand && (
          <span className="sim-filters-note">
            {fmtRiders(ridersPerDay)} riders a day{factor !== 1 ? ` (${fmtRiders(demand!.totals.journeys)} measured)` : ""}, averaged over{" "}
            {demand!.period.days_included} day{demand!.period.days_included === 1 ? "" : "s"}.
          </span>
        )}
      </div>

      {error && <div className="banner banner-error">{error}</div>}
      {loading && !demand && !error && <div className="dm-empty">Loading ridership…</div>}

      {!error && !loading && demand && !hasDemand && (
        <div className="dm-empty">
          <strong>No ridership to run the day on.</strong>
          <p>The demand card above says why; the simulation uses the same riders.</p>
        </div>
      )}

      {hasDemand && runs.length === 0 && (
        <div className="dm-empty">
          <strong>Nothing to run yet.</strong>
          <p>
            {draft
              ? "There is no active timetable for this route and day pattern, and the generator below has no valid draft."
              : "There is no active timetable for this route and day pattern."}
          </p>
        </div>
      )}

      {hasDemand && runs.length > 0 && (
        <div className={`dm-body${loading ? " dm-stale" : ""}`}>
          <Scorecard runs={runs} dirNames={dirNames} />

          <div className="sim-section">
            <DayReplay runs={runs} dirNames={dirNames} onTimeChange={onTimeChange} seek={seek} />
          </div>

          <div className="dm-window" role="group" aria-label="Hours shown in the time–distance diagrams">
            <span className="dm-window-label">Trips drawn for</span>
            <button type="button" className={`dm-chip${win ? "" : " active"}`} onClick={() => setWin(null)}>All day</button>
            {WINDOWS.map((w) => (
              <button key={w.label} type="button"
                className={`dm-chip${win && win[0] === w.range[0] && win[1] === w.range[1] ? " active" : ""}`}
                onClick={() => setWin(w.range)}>
                {w.label} <span className="dm-chip-sub">{fmtHHMM(w.range[0])}–{fmtHHMM(w.range[1] % 1440)}</span>
              </button>
            ))}
          </div>

          <div className="sim-section sim-td-grid">
            {runs.map((run) => (
              <TimeDistanceChart key={run.label} result={run.result} dirNames={dirNames} title={run.label}
                window={win ?? undefined} cursorMin={clock} onPickTime={onPickTime} />
            ))}
          </div>

          <div className="sim-section">
            <WaitingTable runs={runs} dirNames={dirNames} />
          </div>

          <details className="dm-about">
            <summary>About this simulation</summary>
            <ul>
              <li>
                <strong>Riders come to the stop as the fare system saw them come</strong> — each fifteen-minute slice
                spread evenly — wait in the order they arrived, and board the next trip going their way that has room.
                Whoever does not fit waits for the one after. A rider who has waited {patience} minutes without
                getting on leaves, and is counted with those who came after the last trip as "no bus came".
                Riders are averages over the selected days, so fractions of a rider appear.
              </li>
              <li>
                <strong>The timetable is kept exactly.</strong> Trips call at the stop template's times and are never
                late, so what the simulation judges is the timetable, not the traffic. A bus holds {plan.busCapacity} riders
                and is never fuller than that: a trip that would be "500% full" shows up here as riders left at the stop.
              </li>
              <li>
                <strong>Destinations at stops without exit gates are estimated</strong> the same way as on the demand
                card, and riders are placed on trips by the time they reached the stop, not re-timed to the departure.
              </li>
              <li>
                Buses needed are the timetable's blocks; bus-hours run from a block's first departure to its last
                arrival, so time spent turning around is counted as in service.
              </li>
            </ul>
          </details>
        </div>
      )}
    </section>
  );
}
